/* eslint-disable no-console */
import {FormInstance} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';

import {CA_DOCUMENT_TYPE_VALUES} from '../../pages/aiDocumentManager/Constants/commonConstants';
import SelectedCountryContext from '../../pages/configurator/orgFacility/Constants/SelectedCountryContext';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import {updateReferenceRecordId} from '../../services/modules/UploadAndJobs/uploadDocs';
import useUserStore from '../../store/userStore/userStore';
import Loader from '../Loader/Loader';
import processTimeFrameData from '../MonthlyYearly/Utils/processAllTimeFrame';
import PageContent from '../PageContent/PageContent';
import {getFromAndTo, getFromAndToInData} from '../Table/Utils/getFromAndTo';

import getPrerequisiteMasterData from './Common/perquisiteMaster';
import BusinessTravelTicketsBills from './Components/BusinessTravelTickets/BusinessTravelTicketsBills';
import NonRenewableBills from './Components/ElectricityBills/NonRenewableBills';
import RenewableBills from './Components/ElectricityBills/RenewableBills';
import FuelBills from './Components/FuelBills/FuelBills';
import RefrigerantBills from './Components/FuelBills/RefrigerantBills';
import VehicleBills from './Components/FuelBills/VehicleBills';
import CapitalGoodAverageBills from './Components/ProcurementBills/ForCapitalGoods/CapitalGoodAverageBills';
import CapitalGoodSupplierBills from './Components/ProcurementBills/ForCapitalGoods/CapitalGoodSupplierBills';
import RawMaterialAverageBills from './Components/ProcurementBills/ForRawMaterial/RawMaterialAverageBills';
import RawMaterialSupplierBills from './Components/ProcurementBills/ForRawMaterial/RawMaterialSupplierBills';
import DownStreamTransportBills from './Components/ServiceBills/DownStreamTransportBills';
import UpStreamTransportBills from './Components/ServiceBills/UpStreamTransportBills';
import WaterBills from './Components/WaterBills/WaterBills';

interface ITemplateProps {
  isNewRecord: boolean;
  caForm: FormInstance;
  efdbData?: any[];
  allFacilities?: any[];
  record?: any;
  handleResponse: (res: any) => void;
  onFinishFailed: () => void;
  onValuesChange: (status: boolean) => void;
}
const DocumentUploadTemplate: React.FC<ITemplateProps> = ({
  isNewRecord,
  caForm,
  efdbData,
  allFacilities,
  record,
  handleResponse,
  onFinishFailed,
  onValuesChange
}) => {
  const userDetails = useUserStore.getState().userDetails;
  const [isLoading, setIsLoading] = useState(true);

  const [selectedCountry, setSelectedCountry] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  const removeIdForUpdate = (value: any) => {
    const newValue = Object.keys(value).reduce((acc: any, key) => {
      if (
        key !== '_id' &&
        key !== '__v' &&
        key !== 'key' &&
        key !== 'fullAddress'
      ) {
        acc[key] = value[key] === undefined ? null : value[key];
      }

      return acc;
    }, {});

    return newValue;
  };

  useEffect(() => {
    try {
      const selectedFacilityRecord = allFacilities?.[record.year]?.find(
        (item: any) => item?._id === record.facility
      );

      if (selectedFacilityRecord) {
        setSelectedCountry(selectedFacilityRecord?.country);
        setFacilityId(selectedFacilityRecord._id);
        setSelectedYear(selectedFacilityRecord.year);
      }

      isNewRecord && onValuesChange(false);
    } catch {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }

    return () => {
      // Call onValuesChange when component unmounts
      onValuesChange(true);
      caForm.resetFields();
    };
  }, []);

  const componentDetail = useMemo(() => {
    return getPrerequisiteMasterData(record);
  }, [record]);

  const onSubmit = (value: any) => {
    const _id = value?._id ?? caForm.getFieldValue('_id');

    Object.keys(value).forEach((key) => {
      if (value[key] === undefined) {
        value[key] = null; // Replace with a suitable placeholder value
      }
    });

    let ftData;

    const idKey = `${record.facilityId}/${record.year}`;
    const type = componentDetail.type;

    if (value.year && value.facility) {
      ftData = getFromAndToInData(value.year, value.facility);
    } else {
      ftData = getFromAndTo(idKey);
    }

    const {year, fromDate, toDate, facilityId} = ftData;

    const mainData = processTimeFrameData(
      _id ? removeIdForUpdate(value) : value,
      fromDate,
      toDate
    );

    if (type) {
      mainData[type.key] = type.value;
    }

    if (componentDetail.mainAPI && facilityId && year) {
      componentDetail
        .mainAPI(
          _id ? APITypeEnum.PUT : APITypeEnum.POST_NEW,
          {
            ...mainData,
            facility: facilityId,
            year,
            createdBy: userDetails._id,
            updatedBy: userDetails._id
          },
          _id ?? ''
        )
        .then((res: any) => {
          // Call updateReferenceRecordId after mainAPI succeeds
          if (res?.data?._id) {
            updateReferenceRecordId(record._id, res?.data?._id)
              .then(() => {
                handleResponse(res);
              })
              .catch((err) => {
                handleResponse(res);
                onValuesChange(false);
                console.error('Error updating reference record ID:', err);
              });
          }
        })
        .catch(() => {
          onValuesChange(false);
          onFinishFailed();
        });
    }
  };

  const getFormComponent = (componentName: string) => {
    const commonProps = {
      caForm,
      record,
      onFinishFailed,
      onValuesChange,
      onSubmit: onSubmit as any
    };

    switch (componentName) {
      case CA_DOCUMENT_TYPE_VALUES.FOR_MACHINES:
        return <FuelBills {...commonProps} />;
      case CA_DOCUMENT_TYPE_VALUES.FOR_VEHICLE:
        return <VehicleBills {...commonProps} />;
      case CA_DOCUMENT_TYPE_VALUES.FOR_REFRIGERANT:
        return <RefrigerantBills {...commonProps} />;
      case CA_DOCUMENT_TYPE_VALUES.RENEWABLE:
        return <RenewableBills {...commonProps} />;
      case CA_DOCUMENT_TYPE_VALUES.NON_RENEWABLE:
        return <NonRenewableBills {...commonProps} />;
      case CA_DOCUMENT_TYPE_VALUES.RAW_MATERIAL_AVERAGE:
        return <RawMaterialAverageBills {...commonProps} />;
      case CA_DOCUMENT_TYPE_VALUES.RAW_MATERIAL_SUPPLIER_SPECIFIC:
        return <RawMaterialSupplierBills {...commonProps} />;
      case CA_DOCUMENT_TYPE_VALUES.CAPITAL_GOOD_AVERAGE:
        return <CapitalGoodAverageBills {...commonProps} />;
      case CA_DOCUMENT_TYPE_VALUES.CAPITAL_GOOD_SUPPLIER_SPECIFIC:
        return <CapitalGoodSupplierBills {...commonProps} />;
      case CA_DOCUMENT_TYPE_VALUES.UPSTREAM_TRANSPORT:
        return <UpStreamTransportBills {...commonProps} />;
      case CA_DOCUMENT_TYPE_VALUES.DOWNSTREAM_TRANSPORT:
        return <DownStreamTransportBills {...commonProps} />;
      case CA_DOCUMENT_TYPE_VALUES.BUSINESS_TRAVEL_TICKETS:
        return <BusinessTravelTicketsBills {...commonProps} />;
      case CA_DOCUMENT_TYPE_VALUES.WATER_BILLS:
        return <WaterBills {...commonProps} />;
      default:
        return null;
    }
  };

  return isLoading ? (
    <Loader />
  ) : (
    <PageContent>
      <SelectedCountryContext.Provider
        value={{
          selectedCountry,
          efdbData,
          facilityId,
          setSelectedCountry: () => {},
          selectedYear
        }}
      >
        {componentDetail.componentName && (
          <div className="formLayout">
            {getFormComponent(componentDetail.componentName)}
          </div>
        )}
      </SelectedCountryContext.Provider>
    </PageContent>
  );
};

export default DocumentUploadTemplate;
